import type {CreateExtArgs} from '@wix/document-manager-core'
import type {CompRef} from '@wix/document-services-types'
import {getChildrenWithoutPlugins, getLayout} from './utils'
import type {MeasurementsMap, RowsAndColumnsMap} from './types'
import {createCalculateAPI} from './calculateAPI'
import {createMeasurementsMapAPI} from './measurementsMapAPIs'

interface Maps {
    measurementsMap: MeasurementsMap
    rowsAndColumnsMap: RowsAndColumnsMap
}

const toNumber = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

const sanitizeMeasurementsMap = (map: MeasurementsMap): MeasurementsMap => {
    const result: MeasurementsMap = {}
    for (const id of Object.keys(map)) {
        const m = map[id]
        result[id] = {
            width: toNumber(m?.width),
            height: toNumber(m?.height),
            x: toNumber(m?.x),
            y: toNumber(m?.y)
        }
    }
    return result
}

export const createCalculateMeasurementsAPI = ({pointers, extensionAPI}: CreateExtArgs) => {
    const populateMeasurementsMap = (compRef: CompRef, maps: Maps): void => {
        const layoutData = getLayout(extensionAPI, compRef)
        const calculate = createCalculateAPI()
        const {measurementsMap, rowsAndColumnsMap} = maps
        const measurementsMapAPI = createMeasurementsMapAPI(measurementsMap, rowsAndColumnsMap)

        const calculateParams = {calculate, measurementsMapAPI, pointers, extensionAPI, compRef, layoutData}

        calculate.width(calculateParams)
        calculate.columns(calculateParams)
        calculate.height(calculateParams)
        calculate.rows(calculateParams)
        calculate.x(calculateParams)
        calculate.y(calculateParams)

        getChildrenWithoutPlugins(calculateParams).forEach(childRef => {
            populateMeasurementsMap(childRef, maps)
        })
    }

    return (compRef: CompRef): MeasurementsMap => {
        const measurementsMap = {}
        const rowsAndColumnsMap = {}
        populateMeasurementsMap(compRef, {measurementsMap, rowsAndColumnsMap})
        return sanitizeMeasurementsMap(measurementsMap)
    }
}
